<!--
Copyright 2022 Carnegie Mellon University. All Rights Reserved.
Released under a MIT (SEI)-style license, please see LICENSE.md in the
project root for license information or contact permission@sei.cmu.edu for full terms.
-->
<div class="frame">
  <mat-tab-group mat-align-tabs="start" class="tab-group" [selectedIndex]="currentTabIndex" (selectedIndexChange)="onTabIndexChange($event)">
    >
    <mat-tab label="Config" class="tab">
      <div class="edit-buttons">
        <span class="edit-buttons-span">
          <button mat-icon-button (click)="saveChanges()" style="outline: none;" title="Save Changes"
            [disabled]="!isChanged">
            <mat-icon class="mdi-24px self-center" fontIcon="mdi-check"></mat-icon>
          </button>
          <button mat-icon-button (click)="cancelChanges()" style="outline: none;" title="Cancel Changes"
            [disabled]="!isChanged">
            <mat-icon class="mdi-24px self-center" fontIcon="mdi-close"></mat-icon>
          </button>
        </span>
        <span class="change-span">
          @if (isChanged) {
          <mat-label>Changes have not been saved!</mat-label>
          }
        </span>
        <span class="delete-span">
          @if (canManageMsel()) {
          <button mat-icon-button (click)="deleteMsel()" title="Delete this MSEL" [disabled]="msel && msel.status === 'Deployed'">
            <mat-icon class="mdi-18px" fontIcon="mdi-trash-can-outline" alt="Delete"></mat-icon>
          </button>
          }
        </span>
      </div>
      @if (msel) {
      <div class="scrolling-region">
        <div class="info-detail">
          <!-- MSEL Name -->
          <div class="single-detail center-self">
            <mat-form-field class="full-width">
              <mat-label>Name</mat-label>
              <input type="text" matInput [(ngModel)]="msel.name"
                maxlength="70"
                (cut)="isChanged = true" (change)="isChanged = true"
                (paste)="isChanged = true" (keypress)="isChanged = true" [disabled]="!canEditMsel()" />
              <mat-hint align="end">{{ msel.name?.length || 0 }} / 70 characters</mat-hint>
            </mat-form-field>
          </div>
          <!-- MSEL Description -->
          <div class="single-detail center-self">
            <mat-form-field class="full-width">
              <mat-label>Description</mat-label>
              <input type="text" matInput placeholder="Description" [(ngModel)]="msel.description"
                maxlength="600"
                (cut)="isChanged = true" (change)="isChanged = true" (paste)="isChanged = true"
                (keypress)="isChanged = true" [disabled]="!canEditMsel()" />
              <mat-hint align="end">{{ msel.description?.length || 0 }} / 600 characters</mat-hint>
            </mat-form-field>
          </div>
          <!-- Is a Template -->
          <div class="single-detail center-self">
            <div class="sub-detail center-self">
              <mat-checkbox [(ngModel)]="msel.isTemplate" [disabled]="msel.status === 'Deployed' || !canEditMsel()"
                (cut)="isChanged = true" (change)="isChanged = true" (paste)="isChanged = true"
                (keypress)="isChanged = true" title="Mark this MSEL as a reusable template for creating new MSELs">Is a Template</mat-checkbox>
            </div>
          </div>
          <!-- MSEL Start Time -->
          <div class="single-checkbox">
            <mat-checkbox [checked]="msel.startTime?.valueOf() > 0"
              [disabled]="!canEditMsel()"
              (change)="toggleStartTime($event)" title="Schedule a specific start date and time for this MSEL">Set a Start Time
            </mat-checkbox>
          </div>
          @if (hasStartTime()) {
          <div class="single-detail center-self">
            <mat-form-field class="datetime-field">
              <mat-label>Start Date / Time</mat-label>
              <input matInput [ngxMatDatetimePicker]="picker" [(ngModel)]="msel.startTime"
                (dateChange)="isChanged = true" (dateInput)="isChanged = true" style="display: none;" [disabled]="!canEditMsel()">
              <!-- <datetime-picker> -->
              <span (click)="canEditMsel() && picker.open()">
                {{msel.startTime | date: 'dd MMM yyyy HH:mm'}} {{ getTimezoneAbbr() }}
              </span>
              <mat-datepicker-toggle matSuffix title="Choose Date/Time" [for]="picker" [disabled]="!canEditMsel()">
              </mat-datepicker-toggle>
              <ngx-mat-datetime-picker #picker [showSpinners]=true [showSeconds]=false [stepHour]=1 [stepMinute]=5
                [stepSecond]=1 [touchUi]=false [enableMeridian]=true [disableMinute]=false [hideTime]=false
                [color]="'warn'">
                <ngx-mat-datepicker-actions>
                  <button mat-stroked-button ngxMatDatepickerApply>Save</button>
                  <button mat-stroked-button ngxMatDatepickerCancel>Cancel</button>
                </ngx-mat-datepicker-actions>
              </ngx-mat-datetime-picker>
            </mat-form-field>
          </div>
          }
          <!-- MSEL End/Duration -->
          <app-duration-edit [startTime]="msel.startTime" [(durationSeconds)]="msel.durationSeconds"
            [timeName]="'End Date / Time'" [postScript]="'duration'" [canEdit]="canEditMsel()" (change)="isChanged = true;"></app-duration-edit>
          <!-- Integration Section -->
          <div class="integration-div">
            <div class="integration-header">
              <b>Applications to Integrate</b>
              @if ((msel.usePlayer || msel.useCite || msel.useGallery || msel.useSteamfitter) && !msel.isTemplate) {
              <div class="integration-actions">
                @if ((!msel.playerViewId &&
                !msel.citeEvaluationId &&
                !msel.galleryCollectionId &&
                !msel.steamfitterScenarioId &&
                !msel.isTemplate) ||
                pushStatus) {
                <button (click)="pushIntegrations()" title="Push Integrations" class="thin-button" [disabled]="!canManageMsel() ||
                              (msel.useGallery && galleryToDo()) ||
                              (msel.useCite && !msel.citeScoringModelId) ||
                              (msel.useCite && hasCiteTeamsWithoutType()) ||
                              isBusy || isChanged || (pushStatus && pushStatus.startsWith('ERROR'))">@if (pushStatus && !pushStatus.startsWith('ERROR')) {
                  <span>{{ pushStatus }} ...</span>
                  }@if (!pushStatus || pushStatus.startsWith('ERROR')) {
                  <span>Push Integrations</span>
                  }</button>
                }
                @if ((msel.playerViewId ||
                msel.citeEvaluationId ||
                msel.galleryCollectionId ||
                msel.steamfitterScenarioId) &&
                !pushStatus) {
                <button (click)="pullIntegrations()" title="Remove Integrations" class="thin-button"
                  [disabled]="!canManageMsel() || isChanged">Remove
                  Integrations</button>
                }
              </div>
              @if (pushStatus && pushStatus.startsWith('ERROR')) {
              <div class="integration-error" style="color: red; margin-top: 8px; padding: 8px; background-color: #ffebee; border-radius: 4px;">
                <strong>Error:</strong> {{ pushStatus.substring(7) }}
              </div>
              }
              }
            </div>

            <!-- Player Integration -->
            <div class="integration-row">
              <div class="integration-checkbox-cell">
                <mat-checkbox [(ngModel)]="msel.usePlayer" [disabled]="msel.status === 'Deployed' || !canEditMsel()"
                  (cut)="isChanged = true" (change)="isChanged = true" (paste)="isChanged = true"
                  (keypress)="isChanged = true">Player</mat-checkbox>
              </div>
              @if (!msel.isTemplate && msel.usePlayer && msel.playerViewId) {
              <div class="integration-details">
                <div class="integration-field">
                  <span class="integration-label">View:</span>
                  <span class="integration-guid" [title]="msel.playerViewId">{{ msel.playerViewId }}</span>
                  @if (playerViewName) {
                  <span class="integration-name">{{ playerViewName }}</span>
                  }
                  <a [href]="getPlayerViewUrl()" target="_blank" class="integration-link" title="Open in Player">
                    <mat-icon class="mdi-18px" fontIcon="mdi-open-in-new"></mat-icon>
                  </a>
                </div>
              </div>
              }
            </div>

            <!-- Gallery Integration -->
            <div class="integration-row">
              <div class="integration-checkbox-cell">
                <mat-checkbox [(ngModel)]="msel.useGallery" [disabled]="msel.status === 'Deployed' || !canEditMsel()"
                  (cut)="isChanged = true" (change)="isChanged = true" (paste)="isChanged = true"
                  (keypress)="isChanged = true">Gallery</mat-checkbox>
              </div>
              @if (!msel.isTemplate && msel.useGallery && msel.galleryExhibitId) {
              <div class="integration-details">
                @if (msel.galleryCollectionId) {
                <div class="integration-field">
                  <span class="integration-label">Collection:</span>
                  <span class="integration-guid" [title]="msel.galleryCollectionId">{{ msel.galleryCollectionId }}</span>
                  @if (galleryCollectionName) {
                  <span class="integration-name">{{ galleryCollectionName }}</span>
                  }
                </div>
                }
                <div class="integration-field">
                  <span class="integration-label">Exhibit:</span>
                  <span class="integration-guid" [title]="msel.galleryExhibitId">{{ msel.galleryExhibitId }}</span>
                  @if (galleryExhibitName) {
                  <span class="integration-name">{{ galleryExhibitName }}</span>
                  }
                  <a [href]="getGalleryExhibitUrl()" target="_blank" class="integration-link" title="Open in Gallery">
                    <mat-icon class="mdi-18px" fontIcon="mdi-open-in-new"></mat-icon>
                  </a>
                </div>
              </div>
              }
            </div>
            @if (galleryWarningMessage()) {
            <div class="integration-warning">
              <span class="gallery-warning">{{ galleryWarningMessage() }}</span>
            </div>
            }

            <!-- CITE Integration -->
            <div class="integration-row">
              <div class="integration-checkbox-cell">
                <mat-checkbox [(ngModel)]="msel.useCite" [disabled]="msel.status === 'Deployed' || !canEditMsel()"
                  (cut)="isChanged = true" (change)="isChanged = true" (paste)="isChanged = true"
                  (keypress)="isChanged = true">CITE</mat-checkbox>
              </div>
              @if (msel.useCite) {
              <div class="integration-details">
                <div class="integration-field">
                  <span class="integration-label">Scoring Model:</span>
                  @if (scoringModelList.length > 0 && msel.status !== 'Deployed' && canEditMsel()) {
                  <mat-form-field class="integration-select">
                    <mat-select [(ngModel)]="msel.citeScoringModelId" (cut)="isChanged = true"
                      (selectionChange)="isChanged = true; updateCiteScoringModelName()" (paste)="isChanged = true" (keypress)="isChanged = true">
                      <mat-option [value]=""></mat-option>
                      @for (scoringModel of scoringModelList; track scoringModel) {
                      <mat-option [value]="scoringModel.id">
                        {{ scoringModel.description }}
                      </mat-option>
                      }
                    </mat-select>
                  </mat-form-field>
                  }
                  @if (scoringModelList.length === 0 || msel.status === 'Deployed' || !canEditMsel()) {
                    @if (msel.citeScoringModelId) {
                    <span class="integration-guid" [title]="msel.citeScoringModelId">{{ msel.citeScoringModelId }}</span>
                    @if (citeScoringModelName) {
                    <span class="integration-name">{{ citeScoringModelName }}</span>
                    }
                    }
                  }
                </div>
                @if (!msel.isTemplate && msel.citeEvaluationId) {
                <div class="integration-field">
                  <span class="integration-label">Evaluation:</span>
                  <span class="integration-guid" [title]="msel.citeEvaluationId">{{ msel.citeEvaluationId }}</span>
                  @if (citeEvaluationName) {
                  <span class="integration-name">{{ citeEvaluationName }}</span>
                  }
                  <a [href]="getCiteEvaluationUrl()" target="_blank" class="integration-link" title="Open in CITE">
                    <mat-icon class="mdi-18px" fontIcon="mdi-open-in-new"></mat-icon>
                  </a>
                </div>
                }
              </div>
              }
            </div>
            @if (citeWarningMessage()) {
            <div class="integration-warning">
              <span class="gallery-warning">{{ citeWarningMessage() }}</span>
            </div>
            }

            <!-- Steamfitter Integration -->
            <div class="integration-row">
              <div class="integration-checkbox-cell">
                <mat-checkbox [disabled]="msel.status === 'Deployed' || !canEditMsel()" [(ngModel)]="msel.useSteamfitter"
                  (cut)="isChanged = true" (change)="isChanged = true" (paste)="isChanged = true"
                  (keypress)="isChanged = true">Steamfitter</mat-checkbox>
              </div>
              @if (!msel.isTemplate && msel.useSteamfitter && msel.steamfitterScenarioId) {
              <div class="integration-details">
                <div class="integration-field">
                  <span class="integration-label">Scenario:</span>
                  <span class="integration-guid" [title]="msel.steamfitterScenarioId">{{ msel.steamfitterScenarioId }}</span>
                  @if (steamfitterScenarioName) {
                  <span class="integration-name">{{ steamfitterScenarioName }}</span>
                  }
                  <a [href]="getSteamfitterScenarioUrl()" target="_blank" class="integration-link" title="Open in Steamfitter">
                    <mat-icon class="mdi-18px" fontIcon="mdi-open-in-new"></mat-icon>
                  </a>
                </div>
              </div>
              }
            </div>
          </div>
          <!-- Status -->
          <div class="single-detail center-self">
            <mat-form-field class="full-width">
              <mat-label>MSEL Status</mat-label>
              <mat-select [(ngModel)]="msel.status" [disabled]="msel.status === 'Deployed' || !canEditMsel()" (cut)="isChanged = true"
                (change)="isChanged = true" (paste)="isChanged = true" (keypress)="isChanged = true">
                @for (item of itemStatus; track item) {
                <mat-option [value]="item">
                  {{ item }}
                </mat-option>
                }
              </mat-select>
            </mat-form-field>
          </div>
          <!-- Exercise View URL -->
          <div class="url-form-field">
            <div class="editor-label">
              Exercise View URL
              <div class="field-hint">
                Use this URL for direct access to view the Exercise MSEL
              </div>
            </div>
            <div class="url-button-link">
              <button mat-icon-button ngxClipboard [cbContent]="viewUrl" (click)="$event.stopPropagation()"
                title="Copy Exercise View URL:  {{ viewUrl }}">
                <mat-icon class="mdi-24px" fontIcon="mdi-content-copy"></mat-icon>
              </button>
              <a target="_blank" href="{{ viewUrl }}">{{ viewUrl }}</a>
            </div>
          </div>
          <!-- MSEL Starter URL -->
          <div class="url-form-field">
            <div class="editor-label">
              MSEL Starter URL
              <div class="field-hint">
                Use this URL for direct access to edit the Scenario Events
              </div>
            </div>
            <div class="url-button-link">
              <button mat-icon-button ngxClipboard [cbContent]="starterUrl" (click)="$event.stopPropagation()"
                title="Copy MSEL Starter URL:  {{ starterUrl }}">
                <mat-icon class="mdi-24px" fontIcon="mdi-content-copy"></mat-icon>
              </button>
              <a target="_blank" href="{{ starterUrl }}">{{ starterUrl }}</a>
            </div>
          </div>
          <!-- row meta data -->
          @if (canManageMsel()) {
          <div class="single-detail center-self">
            <mat-form-field class="full-width"
              title="A numeric value that defines the height of the header row when this MSEL is exported as an xlsx file">
              <mat-label>Header Row Metadata (Height) when exported as an Excel file</mat-label>
              <input type="text" matInput
                [(ngModel)]="msel.headerRowMetadata" (cut)="isChanged = true" (change)="isChanged = true"
                (paste)="isChanged = true" (keypress)="isChanged = true"
                title="An integer value that defines the height of the header row when this MSEL is exported as an xlsx file" />
            </mat-form-field>
          </div>
          }
          <div class="single-detail center-self">
            <mat-form-field class="full-width">
              <mat-label>Date Created</mat-label>
              <input type="text" matInput value="{{ msel.dateCreated | date: 'yyyy-MM-dd' }}"
                disabled />
            </mat-form-field>
          </div>
          <div class="single-detail center-self">
            <mat-form-field class="full-width">
              <mat-label>Created By</mat-label>
              <input type="text" matInput value="{{ getUserName(msel.createdBy) }}" disabled />
            </mat-form-field>
          </div>
        </div>
      </div>
      }
    </mat-tab>
    @for (page of mselPages; track page.id) {
    <mat-tab label="{{ page.name }}" class="tab">
      <!-- page content -->
      <div class="edit-buttons">
        @if (editingPageId !== page.id) {
        <span>
          @if (canManageMsel()) {
          <button mat-icon-button (click)="requestPageEdit(page.id)" style="outline: none;" title="Edit Page">
            <mat-icon class="mdi-24px self-center" fontIcon="mdi-square-edit-outline"></mat-icon>
          </button>
          }
          <button mat-icon-button (click)="deletePage(page); $event.stopPropagation()" style="outline: none;"
            title="Delete {{ page.name }}">
            <mat-icon class="mdi-24px self-center" fontIcon="mdi-trash-can-outline"></mat-icon>
          </button>
          <button mat-icon-button ngxClipboard [cbContent]="basePageUrl + page.id" (click)="$event.stopPropagation()"
            title="Copy page URL">
            <mat-icon class="mdi-24px" fontIcon="mdi-content-copy"></mat-icon>
          </button>
          <button mat-icon-button (click)="openContent(page.id)" title="Open page in new tab" class="far-right-button">
            <mat-icon class="mdi-24px self-center" fontIcon="mdi-open-in-new"></mat-icon>
          </button>
          @if (page.allCanView) {
          <span>&nbsp;&nbsp;** All MSEL users can view this page</span>
          }
        </span>
        }
        @if (editingPageId === page.id) {
        <span>
          <button mat-icon-button (click)="saveMselPageEdits()" style="outline: none;" title="Save Changes">
            <mat-icon class="mdi-24px self-center" fontIcon="mdi-check"></mat-icon>
          </button>
          <button mat-icon-button (click)="cancelMselPageEdits()" style="outline: none;" title="Cancel Changes">
            <mat-icon class="mdi-24px self-center" fontIcon="mdi-close"></mat-icon>
          </button>
          @if (hasUnsavedPageChanges()) {
          <mat-label class="unsaved-warning">Changes have not been saved!</mat-label>
          }
        </span>
        }
      </div>
      @if (msel) {
      <div class="page-content-region">
        @if (editingPageId === page.id) {
        <mat-form-field class="full-width">
          <input type="text" matInput placeholder="Page Name" [(ngModel)]="changedMselPage.name"
            [disabled]="editingPageId !== page.id" />
        </mat-form-field>
        }
        <div class="all-can-view-div">
          @if (editingPageId === page.id) {
          <mat-checkbox [(ngModel)]="changedMselPage.allCanView" title="All MSEL users can view this page">All MSEL
            users can view this page</mat-checkbox>
          } @else if (page.allCanView) {
          <span>** All MSEL users can view this page</span>
          }
        </div>
        <div>
          @if (editingPageId === page.id) {
          <angular-editor [placeholder]="'Content'" [(ngModel)]="changedMselPage.content"
            [config]="editorConfig"></angular-editor>
          } @else {
          <angular-editor [placeholder]="'Content'" [ngModel]="page.content"
            [config]="viewConfig"></angular-editor>
          }
        </div>
      </div>
      }
    </mat-tab>
    }
    @if (canManageMsel()) {
    <mat-tab>
      <ng-template mat-tab-label>
        <mat-icon class="mdi-24px" fontIcon="mdi-plus-circle" title="Add MSEL Notes Page"></mat-icon>
        Add Page
      </ng-template>
      <div class="page-content-region">
        <p>Click this tab to create a new page.</p>
      </div>
    </mat-tab>
    }
  </mat-tab-group>
</div>
